import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { Search, X, TrendingUp, User as UserIcon, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, startAt, endAt, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { TrendingSection } from '../TrendingSection';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useBackButton } from '../../hooks/useBackButton';

interface SearchUser {
  id: string;
  displayName: string;
  username?: string;
  avatar?: string;
  bio?: string;
}

interface MobileSearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserAvatar: React.FC<{ user: SearchUser }> = ({ user }) => {
  const [errored, setErrored] = useState(false);
  const letter = (user.displayName || user.username || 'U').trim().charAt(0).toUpperCase();

  if (user.avatar && !errored) {
    return (
      <img
        src={user.avatar}
        alt={user.displayName}
        className="w-10 h-10 rounded-full object-cover"
        onError={() => setErrored(true)}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-white font-semibold text-sm">
      {letter}
    </div>
  );
};

export const MobileSearchOverlay: React.FC<MobileSearchOverlayProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query_str, setQuery] = useState('');
  const [results, setResults] = useState<SearchUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const dragControls = useDragControls();

  // Smooth dismiss handler
  const handleSmoothClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 220);
  }, [isClosing, onClose]);

  // Reset closing state when opening
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  // Lock body scroll when search is open
  useScrollLock({
    isLocked: isOpen && !isClosing,
    preserveScrollbarWidth: true,
  });

  // Hardware back button & Escape support for closing search overlay instead of app
  useBackButton(isOpen && !isClosing, handleSmoothClose);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    } else {
      setQuery('');
      setResults([]);
    }
  }, [isOpen]);

  // Debounced Firestore user search
  const searchUsers = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const usersColl = collection(db, 'users');
      const lower = trimmed.toLowerCase();

      // Search by username prefix AND displayName prefix, merge results
      const [byUsername, byDisplay] = await Promise.all([
        getDocs(query(usersColl, orderBy('username'), startAt(lower), endAt(lower + '\uf8ff'), limit(6))).catch(() => null),
        getDocs(query(usersColl, orderBy('displayName'), startAt(trimmed), endAt(trimmed + '\uf8ff'), limit(6))).catch(() => null),
      ]);

      const seen = new Map<string, SearchUser>();
      const add = (snap: any) => {
        if (!snap) return;
        snap.forEach((d: any) => {
          const data = d.data();
          const displayName = String(data?.displayName || data?.username || '').trim();
          if (!displayName || seen.has(d.id)) return;
          seen.set(d.id, {
            id: d.id,
            displayName,
            username: data?.username ? String(data.username) : undefined,
            avatar: typeof data?.avatar === 'string' ? data.avatar : undefined,
            bio: typeof data?.bio === 'string' ? data.bio : undefined,
          });
        });
      };
      add(byUsername);
      add(byDisplay);

      setResults(Array.from(seen.values()).slice(0, 8));
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!query_str.trim()) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = window.setTimeout(() => searchUsers(query_str), 300);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [query_str, searchUsers]);

  const handleUserClick = (user: SearchUser) => {
    handleSmoothClose();
    const path = user.username ? `/profile/${user.username}` : `/profile/${user.id}`;
    navigate(path);
  };

  const handleSeeAllResults = () => {
    if (!query_str.trim()) return;
    handleSmoothClose();
    navigate(`/explore?q=${encodeURIComponent(query_str)}&tab=accounts`);
  };

  const handleHashtagClick = (hashtag: string) => {
    handleSmoothClose();
    navigate(`/explore?q=${encodeURIComponent(hashtag)}&tab=posts`);
  };

  const handleCreatorClick = (creatorId: string) => {
    handleSmoothClose();
    navigate(`/profile/${creatorId}`);
  };

  const handlePostClick = (postId: string) => {
    handleSmoothClose();
    navigate(`/post/${postId}`);
  };

  const handleEventClick = (eventId: string) => {
    handleSmoothClose();
    navigate(`/events/${eventId}`);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-[60] lg:hidden touch-none"
            onClick={handleSmoothClose}
          />

          {/* Sheet with drag-to-dismiss */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: isClosing ? '100%' : 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.7 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 60 || info.velocity.y > 200) {
                handleSmoothClose();
              }
            }}
            className="fixed inset-x-0 bottom-0 top-14 z-[61] lg:hidden flex flex-col bg-white dark:bg-dark-50 rounded-t-2xl shadow-2xl overflow-hidden touch-pan-y"
          >
            {/* Top Handle & Drag Zone */}
            <div
              className="touch-none select-none cursor-grab active:cursor-grabbing flex-shrink-0"
              onPointerDown={(e) => dragControls.start(e)}
            >
              {/* Handle bar */}
              <div
                className="flex justify-center pt-3 pb-1"
                onClick={handleSmoothClose}
              >
                <div className="w-12 h-1.5 bg-neutral-300 dark:bg-dark-300 rounded-full hover:bg-neutral-400 dark:hover:bg-dark-400 transition-colors" />
              </div>

              {/* Search bar */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 dark:border-dark-200">
                <div className="flex-1 flex items-center gap-2 bg-neutral-100 dark:bg-dark-100 rounded-xl px-3 py-2.5">
                  <Search size={18} className="text-neutral-400 dark:text-dark-400 flex-shrink-0" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query_str}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search artists, users..."
                    className="flex-1 bg-transparent text-sm text-neutral-900 dark:text-dark-900 placeholder:text-neutral-400 dark:placeholder:text-dark-400 outline-none"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {query_str && (
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      className="flex-shrink-0 text-neutral-400 hover:text-neutral-600 dark:text-dark-400 dark:hover:text-dark-700 touch-manipulation"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleSmoothClose}
                  className="text-sm font-medium text-violet-600 dark:text-violet-400 touch-manipulation whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto pb-16 overscroll-contain">
              <AnimatePresence mode="wait">
                {/* --- Search Results --- */}
                {query_str.trim() ? (
                  <motion.div
                    key="results"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="p-4"
                  >
                    {isSearching ? (
                      /* Skeleton */
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center gap-3 animate-pulse">
                            <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-dark-200" />
                            <div className="flex-1 space-y-1.5">
                              <div className="h-3.5 bg-neutral-200 dark:bg-dark-200 rounded-full w-32" />
                              <div className="h-3 bg-neutral-200 dark:bg-dark-200 rounded-full w-20" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : results.length > 0 ? (
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-neutral-400 dark:text-dark-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                          <UserIcon size={12} /> Users
                        </p>
                        {results.map((user, i) => (
                          <motion.button
                            key={user.id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04 }}
                            onClick={() => handleUserClick(user)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-neutral-100 dark:hover:bg-dark-100 active:bg-neutral-200 dark:active:bg-dark-200 transition-colors touch-manipulation text-left"
                          >
                            <UserAvatar user={user} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-neutral-900 dark:text-dark-900 truncate">
                                {user.displayName}
                              </p>
                              {user.username && (
                                <p className="text-xs text-neutral-500 dark:text-dark-400 truncate">
                                  @{user.username}
                                </p>
                              )}
                              {user.bio && (
                                <p className="text-xs text-neutral-400 dark:text-dark-400 truncate mt-0.5">
                                  {user.bio}
                                </p>
                              )}
                            </div>
                            <ArrowRight size={16} className="text-neutral-300 dark:text-dark-300 flex-shrink-0" />
                          </motion.button>
                        ))}

                        {/* See all results */}
                        <button
                          onClick={handleSeeAllResults}
                          className="w-full mt-3 py-3 text-sm font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-xl transition-colors touch-manipulation flex items-center justify-center gap-2"
                        >
                          See all results for "{query_str}"
                          <ArrowRight size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-14 h-14 rounded-full bg-neutral-100 dark:bg-dark-100 flex items-center justify-center mb-4">
                          <Search size={24} className="text-neutral-400 dark:text-dark-400" />
                        </div>
                        <p className="text-sm font-medium text-neutral-600 dark:text-dark-500">
                          No users found for "{query_str}"
                        </p>
                        <p className="text-xs text-neutral-400 dark:text-dark-400 mt-1">
                          Try a different name or username
                        </p>
                        <button
                          onClick={handleSeeAllResults}
                          className="mt-4 px-4 py-2 text-sm font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 rounded-xl touch-manipulation"
                        >
                          Search in Explore →
                        </button>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  /* --- Trending (default state) --- */
                  <motion.div
                    key="trending"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="px-4 pt-4 pb-24"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp size={18} className="text-violet-600 dark:text-violet-400" />
                      <h3 className="text-base font-bold text-neutral-900 dark:text-dark-900">
                        Trending
                      </h3>
                    </div>
                    <TrendingSection
                      onHashtagClick={handleHashtagClick}
                      onCreatorClick={handleCreatorClick}
                      onPostClick={handlePostClick}
                      onEventClick={handleEventClick}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileSearchOverlay;
